import { useAtomValue } from 'jotai'
import { ReactNode, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { StorageKeys } from '~/atoms/StorageKeys'
import { usePartnerDesktopAppName } from '~/hooks/useCurrentPartner'
import { useElectronUpdaterContext } from '~/providers/ElectronUpdaterContext'
import { observe } from '~/services/observability/observe'
import { AnalyticsNamespace, DesktopAnalyticsEvents } from '~/types/Analytics'

import type { UpdateRequirement } from '@mangovoice/types'

import { RequiredUpdateScreen } from './RequiredUpdateScreen'
import { hasActiveCallAtom } from './updateRequirementState'

interface RequiredUpdateShownRecord {
  fromVersion: string
  minimumVersion: string | null
}

const readShownRecord = (): RequiredUpdateShownRecord | null => {
  try {
    const raw = localStorage.getItem(StorageKeys.RequiredUpdateShown)
    return raw ? (JSON.parse(raw) as RequiredUpdateShownRecord) : null
  } catch {
    return null
  }
}

const writeShownRecord = (record: RequiredUpdateShownRecord | null) => {
  try {
    if (record) {
      localStorage.setItem(
        StorageKeys.RequiredUpdateShown,
        JSON.stringify(record),
      )
    } else {
      localStorage.removeItem(StorageKeys.RequiredUpdateShown)
    }
  } catch {
    // Storage can be unavailable; the completion event is best effort.
  }
}

const useRequiredUpdateTracking = (
  requirement: UpdateRequirement | null,
  isBlocked: boolean,
) => {
  const trackedShownRef = useRef(false)

  useEffect(() => {
    if (!isBlocked || !requirement || trackedShownRef.current) {
      return
    }
    trackedShownRef.current = true
    writeShownRecord({
      fromVersion: requirement.currentVersion,
      minimumVersion: requirement.minimumVersion,
    })
    observe.track(
      AnalyticsNamespace.DESKTOP,
      DesktopAnalyticsEvents.UpdateRequiredShown,
      {
        current_version: requirement.currentVersion,
        minimum_version: requirement.minimumVersion,
      },
    )
  }, [isBlocked, requirement])

  useEffect(() => {
    if (!requirement || requirement.level === 'required') {
      return
    }
    const record = readShownRecord()
    if (
      !record ||
      !requirement.currentVersion ||
      record.fromVersion === requirement.currentVersion
    ) {
      return
    }
    writeShownRecord(null)
    observe.track(
      AnalyticsNamespace.DESKTOP,
      DesktopAnalyticsEvents.UpdateRequiredCompleted,
      {
        previous_version: record.fromVersion,
        current_version: requirement.currentVersion,
        minimum_version: record.minimumVersion,
      },
    )
  }, [requirement])
}

/**
 * Replaces the whole app with the blocking screen when the desktop build is
 * below the channel's minimumVersion. Unmounting the router also unmounts
 * PhoneHub, which unregisters SIP, so new calls go to other devices and the
 * popup has nothing to ring for. Waits for the call list to empty first.
 */
export const RequiredUpdateGate = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const appName = usePartnerDesktopAppName()
  const { updateRequirement } = useElectronUpdaterContext()
  const hasActiveCall = useAtomValue(hasActiveCallAtom)
  const isBlocked = updateRequirement?.level === 'required' && !hasActiveCall

  useRequiredUpdateTracking(updateRequirement, isBlocked)

  useEffect(() => {
    if (!isBlocked) {
      return
    }
    // The block unmounts PhoneHub in the render that empties the call list,
    // before PhoneHub publishes it. Without this, main keeps the last call:
    // the popup rings on and quitting warns about an active call.
    const sharedVariables = window.electron?.sharedVariables
    sharedVariables?.setValue('CallList', [])
    sharedVariables?.setValue('ActiveCallUuid', null)
    sharedVariables?.setValue('PendingInvitationUuid', null)
    sharedVariables?.setValue('SipConnected', false)
    window.electron?.send('hide-popup-window')
    window.electron?.updates?.alertRequired?.({
      title: t('updateRequirement.alertTitle', {
        appName,
        defaultValue: '{{appName}} needs an update',
      }),
      body: t(
        'updateRequirement.alertBody',
        'Calls go to other devices or voicemail until you update.',
      ),
    })
    // Fires when the block starts; a language change must not re-alert.
  }, [isBlocked])

  if (isBlocked && updateRequirement) {
    return <RequiredUpdateScreen requirement={updateRequirement} />
  }
  return <>{children}</>
}
